import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";

@Injectable({ providedIn: "root" })
export class CookieService {
    constructor(
        @Inject(DOCUMENT) private readonly doc: Document,
        @Inject(PLATFORM_ID) private readonly platformId: object
    ) {}

    private get canUseCookies(): boolean {
        return isPlatformBrowser(this.platformId) && !!this.doc?.cookie;
    }

    public get(name: string): string | null {
        if (!this.canUseCookies) return null;
        const encodedName = encodeURIComponent(name) + "=";
        const parts = this.doc.cookie.split("; ");
        for (const part of parts) {
            if (part.startsWith(encodedName)) {
                const raw = part.substring(encodedName.length);
                try {
                    return decodeURIComponent(raw);
                } catch {
                    return raw;
                }
            }
        }
        return null;
    }

    public has(name: string): boolean {
        if (!this.canUseCookies) return false;
        const encodedName = encodeURIComponent(name) + "=";
        return this.doc.cookie.split("; ").some(p => p.startsWith(encodedName));
    }

    public set(
        name: string,
        value: string,
        options: {
            path?: string;
            expires?: Date;
            maxAge?: number;
            secure?: boolean;
            sameSite?: "Lax" | "Strict" | "None";
        } = {}
    ): void {
        if (!this.canUseCookies) return;

        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        if (options.maxAge != null) cookie += `; Max-Age=${Math.floor(options.maxAge)}`;
        if (options.expires) cookie += `; Expires=${options.expires.toUTCString()}`;
        cookie += `; Path=${options.path ?? "/"}`;
        if (options.sameSite) cookie += `; SameSite=${options.sameSite}`;
        if (options.secure) cookie += `; Secure`;

        this.doc.cookie = cookie;
    }

    public delete(name: string, path: string = "/"): void {
        if (!this.canUseCookies) return;
        this.set(name, "", { path, expires: new Date(0) });
    }
}
